import {
  useFindAllCompaniesQuery,
  useFindAllHardwareQuery,
  useFindAllLicensesQuery,
  useFindAllModulesQuery,
  useFindAllOrdersQuery,
  useFindAllSoftwareQuery,
  useFindOneLicenseQuery,
  useRemoveLicenseMutation,
} from "../../api/api-hooks";
import { Grid, IconButton, TextField, Typography } from "@mui/material";
import { Autocomplete } from "@mui/material";
import ItemOptionsBar from "../../components/ItemOptionsBar";
import { nullStringCheck } from "../../utils/stringManipulation";
import { Item } from "../../utils/sharedTypes";
import { SoftwareSoldItemForm } from "./softwareSoldTypes";
import { itemValidator } from "./softwareSoldUtils";
import useSoftwareSoldMiddleware from "./useSoftwareSoldMiddleware";
import useLicenseItemCreator from "./useLicenseItemCreator";
import useLicenseItemUpdater from "./useLicenseItemUpdater";
import SoftwareTypeManagementButton from "../software-types/SoftwareTypeManagementButton";
import HardwareManagementButton from "../hardware/HardwareManagementButton";
import { DeleteOutline, AddCircleOutline } from "@mui/icons-material";

interface Props {
  selectedSoftware: Item[];
  setSelectedSoftware: React.Dispatch<React.SetStateAction<Item[]>>;
  editModalOpenFlag: boolean;
  setEditModalOpenFlag: React.Dispatch<React.SetStateAction<boolean>>;
}

const addFormInitialState: SoftwareSoldItemForm = {
  hardware: null,
  company: null,
  license: undefined,
  software: null,
  order: null,
  module: null,
  modulesToAdd: [],
  notes: "",
};

const SoftwareSoldOptionsBar = ({
  selectedSoftware,
  setSelectedSoftware,
  editModalOpenFlag,
  setEditModalOpenFlag,
}: Props) => {
  const [deleteSoftware] = useRemoveLicenseMutation();
  const itemCreator = useLicenseItemCreator();
  const itemUpdater = useLicenseItemUpdater();
  const { data: companies } = useFindAllCompaniesQuery({});
  const { data: orders } = useFindAllOrdersQuery({});
  const { data: softwareTypes } = useFindAllSoftwareQuery({});
  const { data: allHardware } = useFindAllHardwareQuery({});
  const { data: allLicenses } = useFindAllLicensesQuery({});
  const { data: allModules } = useFindAllModulesQuery({});
  const softwareSoldHandlerMiddleware = useSoftwareSoldMiddleware();
  if (
    companies === undefined ||
    softwareTypes === undefined ||
    allHardware === undefined ||
    orders === undefined ||
    allLicenses === undefined ||
    allModules === undefined
  )
    return null;
  const filteredOrders = orders.filter((order) => !order.completed);
  return (
    <>
      <ItemOptionsBar
        selectedItems={selectedSoftware}
        setSelectedItems={setSelectedSoftware}
        handlerMiddleware={softwareSoldHandlerMiddleware}
        editModalOpen={editModalOpenFlag}
        setEditModalOpen={setEditModalOpenFlag}
        itemValidator={itemValidator}
        formInstructions="Company, license, hardware, and software type are required, and if modules are to be added, module type and license must be filled in."
        addFormInitialState={addFormInitialState}
        itemName="Software Sold"
        deleteNotes="Deleting these software sold entries will also delete all associated module licenses, if any."
        childrenCreator={(addFormState: SoftwareSoldItemForm, handler: any) => (
          <Grid container spacing={3}>
            <Grid container item xs direction="column" spacing={1}>
              <Grid style={{ marginBottom: ".3rem" }} item>
                <Autocomplete
                  onChange={(event, value) => {
                    handler(event, value, "company");
                  }}
                  value={addFormState.company}
                  options={companies}
                  getOptionLabel={(company) => company.companyName}
                  style={{ width: 300, marginTop: ".5rem" }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select Company"
                      name="company"
                      variant="outlined"
                      id="company"
                    />
                  )}
                />
              </Grid>
              <Grid item>
                <Autocomplete
                  onChange={(event, value) => {
                    handler(event, value, "software");
                  }}
                  value={addFormState.software}
                  options={softwareTypes}
                  getOptionLabel={(type) => type.type}
                  style={{ width: 300 }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select Software Type"
                      variant="outlined"
                    />
                  )}
                />
              </Grid>
              <Grid item>
                <SoftwareTypeManagementButton />
              </Grid>
              <Grid item>
                <Autocomplete
                  onChange={(event, value) => {
                    handler(event, value, "hardware");
                  }}
                  value={addFormState.hardware}
                  options={allHardware}
                  getOptionLabel={(hardware) => hardware.serial}
                  style={{ width: 300 }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select Hardware"
                      variant="outlined"
                    />
                  )}
                />
              </Grid>
              <Grid item>
                <HardwareManagementButton />
              </Grid>
            </Grid>

            <Grid container item xs direction="column" spacing={1}>
              <Grid item>
                <Autocomplete
                  onChange={(event, value) => {
                    handler(event, value, "order");
                  }}
                  value={addFormState.order}
                  options={filteredOrders}
                  getOptionLabel={(order) =>
                    order.purchaseOrderName
                      ? order.purchaseOrderName
                      : order.orderDate
                  }
                  style={{ width: 300 }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select Order"
                      variant="outlined"
                    />
                  )}
                />
              </Grid>
              <Grid item>
                <TextField
                  margin="dense"
                  name="license"
                  label="License"
                  fullWidth
                  value={nullStringCheck(addFormState.license)}
                  onChange={handler}
                />
              </Grid>
              {addFormState.software?.type.toLowerCase().includes("spectras") &&
                addFormState.modulesToAdd && (
                  <>
                    <Grid container item spacing={1}>
                      <Grid item style={{ marginBottom: ".7rem" }}>
                        <Typography variant="subtitle1">
                          Add Modules:
                        </Typography>
                      </Grid>
                      {addFormState.modulesToAdd.map((module, idx) => (
                        <Grid item key={idx} container direction="column">
                          <Grid
                            item
                            container
                            spacing={1}
                            alignItems="center"
                            style={{
                              marginLeft: "1.5rem",
                              width: "100%",
                            }}
                          >
                            <Grid item>
                              <Autocomplete
                                onChange={(event, value) => {
                                  handler(event, value, `modulesToAdd${idx}`);
                                }}
                                value={module.module}
                                options={allModules}
                                getOptionLabel={(module) => module.moduleName}
                                style={{ width: 300 }}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    label="Select Module"
                                    variant="outlined"
                                  />
                                )}
                              />
                            </Grid>
                            <Grid item>
                              <IconButton
                                aria-label="Remove module"
                                onClick={(event) =>
                                  handler(event, idx, "deleteModule")
                                }
                                size="large"
                              >
                                <DeleteOutline color="error" />
                              </IconButton>
                            </Grid>
                          </Grid>
                          <Grid
                            item
                            style={{
                              marginLeft: "1.5rem",
                              marginRight: ".5rem",
                            }}
                          >
                            <TextField
                              margin="dense"
                              name={`moduleLicense${idx}`}
                              label="License"
                              fullWidth
                              value={nullStringCheck(
                                addFormState.modulesToAdd[idx].license
                              )}
                              onChange={handler}
                            />
                          </Grid>
                        </Grid>
                      ))}
                    </Grid>
                    <Grid item style={{ marginLeft: "3rem" }}>
                      <IconButton
                        aria-label="Add module"
                        onClick={(event: React.MouseEvent) => {
                          handler(event, null, "newModule");
                        }}
                        size="large"
                      >
                        <AddCircleOutline color="primary" />
                      </IconButton>
                    </Grid>
                  </>
                )}
              <Grid item>
                <TextField
                  margin="dense"
                  name="notes"
                  label="Notes"
                  multiline
                  fullWidth
                  value={nullStringCheck(addFormState.notes)}
                  onChange={handler}
                  rows={5}
                />
              </Grid>
            </Grid>
          </Grid>
        )}
        itemCreator={itemCreator}
        itemGetter={useFindOneLicenseQuery}
        itemUpdater={itemUpdater}
        itemDeleter={async (idObject: { id: number }) => {
          return deleteSoftware(idObject);
        }}
      />
    </>
  );
};

export default SoftwareSoldOptionsBar;
